import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { TicketFilterAnfrage, TicketKarte, TicketSpalte, Unternehmen } from "@kundeninfo/api-client";
import { Chip, Hinweis, Knopf, Optionen, Tabelle, Werkzeuggruppe, Werkzeugleiste } from "@kundeninfo/ui/me-design";
import { KiKnopf, Suchfeld } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { berlinerTag, datumsMarke, tageZwischen } from "./ticketFormat.js";
import { Avatar } from "./Avatar.js";
import { TicketFilterleiste, anzahlAktiverFilter, filterAnfrage } from "./TicketFilterleiste.js";
import { LEERER_FILTER, leseFilter, merkeFilter } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";
import "./Tickets.css";

/**
 * DIE LISTE (Spec „Liste“): `Tabelle stapeln` (am Telefon gestapelt), Ordnung vom Server
 * (Schlechtestes oben, Erledigte am Ende), die Filter des Boards (Ruling A4, dasselbe Gedächtnis
 * `boardSpeicher.ts` — oder von außen über `filter`/`onFilter`), Suche über Nummer und Titel,
 * „Archiv einbeziehen“ (120) — seit 1d-B alle in der Werkzeugleiste „Werkzeuge der Liste“ (204, F129:
 * Gruppen, „Zurücksetzen“ und Telefonblatt macht me-werkzeugleiste), „+ Aufgabe“ als Hauptaktion
 * (`onAnlegen`), 100 je Seite mit „Weitere laden“; das Rollfeld bleibt beim
 * Nachladen dasselbe Element (Scroll bleibt, Hausregel). In der Akte (`kundeId`, Spec
 * „Kundenakte: Reiter ‚Tickets‘“) die Tickets des Kunden ohne Seiten, Filter und Suche, kompakt in
 * fünf Spalten: ohne „Kunden“ (immer derselbe), „Alter“ und „Verknüpfungen“, das Unternehmen als
 * Chip vor der Nummer (Review 19).
 * Titel sind reiner Text (React escaped, Review-Fokus 3) und stehen als Verweis in Textfarbe links
 * (Sichtprobe 9). Die Titelspalte hat eine Mindestbreite: In der schmalen Mittelspalte der Akte
 * rollt die Tabelle dann waagerecht in ihrem Rahmen, statt den Titel Zeichen für Zeichen
 * umzubrechen (Sichtprobe 1); nur die breite Liste bricht lange Wörter überall um.
 */

export type TicketListeProps = {
  client: TicketsClient; companies: readonly Unternehmen[]; onOeffnen: (id: string) => void;
  kundeId?: string; neuLaden?: number; ichId?: string;
  /** Filter von außen (Board und Liste im Bereich teilen ihn, Aufgabe 17); ohne ihn der gemerkte. */
  filter?: BoardFilter;
  /** Bekommt die Änderung als Funktion auf den jüngsten Stand (wie ein `setState`-Updater). */
  onFilter?: (aendern: (alt: BoardFilter) => BoardFilter) => void;
  /** „+ Aufgabe“ in der Werkzeugleiste (204); ohne den Rückruf gibt es den Knopf nicht. */
  onAnlegen?: () => void;
};

/** Jede Kennung einmal, in der Reihenfolge des ersten Auftretens. */
function ohneDoppelte(zeilen: readonly TicketKarte[]): TicketKarte[] {
  const gesehen = new Set<string>();
  return zeilen.filter((t) => (gesehen.has(t.id) ? false : (gesehen.add(t.id), true)));
}

export function TicketListe(p: TicketListeProps): ReactElement {
  const [items, setItems] = useState<TicketKarte[] | null>(null);
  // Gibt es hinter der letzten geladenen Seite noch eine? Nach Seiten gerechnet, nicht nach
  // Zeilen — die sind ohne Doppelte womöglich weniger als `total`.
  const [mehr, setMehr] = useState(false);
  const [seite, setSeite] = useState(1);
  const [archiv, setArchiv] = useState(false);
  const [q, setQ] = useState("");
  const [spalten, setSpalten] = useState<TicketSpalte[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [eigenerFilter, setEigenerFilter] = useState<BoardFilter>(() => leseFilter(p.companies.map((c) => c.id)));
  const filter = p.filter ?? eigenerFilter;
  const akte = p.kundeId !== undefined;
  const ich = useMemo(() => (p.ichId === undefined ? null : { id: p.ichId, name: "Ich" }), [p.ichId]);

  // Die Anfrage aus den vier Filtern, stabil über ihre Werte (ein von außen neu gebautes, gleiches
  // Filterobjekt lädt nicht neu).
  const { company, customer, assignee, linked } = filter;
  const anfrage: TicketFilterAnfrage = useMemo(() => filterAnfrage({ company, customer, assignee, linked }),
    [company, customer, assignee, linked]);

  useEffect(() => {
    let aktiv = true;
    p.client.listTicketColumns().then((r) => { if (aktiv) setSpalten(r.items); }).catch(() => undefined);
    return () => { aktiv = false; };
  }, [p.client]);

  // Nur die jüngste Anfrage zeichnet (wie im Board): Tippen in der Suche oder ein Filterwechsel
  // darf nicht von einer älteren Antwort überholt werden.
  const lauf = useRef(0);
  // Die Seiten `von` bis `bis` (P3-Nachlese): „Weitere laden" holt eine Seite dazu, ein Neuladen
  // nach einer Änderung alle bis dahin geladenen — sonst schnurrte die Liste auf Seite 1
  // zusammen. Zeilen gibt es je Kennung nur einmal: Verschiebt sich die Ordnung zwischen zwei
  // Seiten, stünde ein Ticket sonst doppelt da.
  const laden = useCallback(async (von: number, bis: number = von) => {
    const nummer = ++lauf.current;
    try {
      if (p.kundeId !== undefined) {
        const r = await p.client.listCustomerTickets(p.kundeId, archiv);
        if (nummer !== lauf.current) return;
        setItems(r.items); setMehr(false); setSeite(1);
      } else {
        const basis: TicketFilterAnfrage = { ...anfrage, ...(archiv ? { archive: true } : {}), ...(q !== "" ? { q } : {}) };
        const seiten = await Promise.all(Array.from({ length: bis - von + 1 }, (_, i) => von + i)
          .map((s) => p.client.listTickets({ ...basis, ...(s > 1 ? { page: s } : {}) })));
        if (nummer !== lauf.current) return;
        const neu = seiten.flatMap((r) => r.items);
        setItems((alt) => ohneDoppelte(von > 1 && alt !== null ? [...alt, ...neu] : neu));
        const letzte = seiten[seiten.length - 1];
        setMehr(letzte !== undefined && letzte.items.length > 0 && bis * letzte.proSeite < letzte.total);
        setSeite(bis);
      }
      setFehler(null);
    } catch {
      if (nummer === lauf.current) setFehler("Die Tickets konnten nicht geladen werden.");
    }
  }, [p.client, p.kundeId, archiv, q, anfrage]);

  // Neue Anfrage (Filter, Suche, Archiv) → Seite 1; nur `neuLaden` geändert → alle geladenen
  // Seiten neu, die Liste behält ihre Länge (und das Rollfeld seine Position).
  const seiteRef = useRef(seite);
  seiteRef.current = seite;
  const vorher = useRef({ laden, neuLaden: p.neuLaden });
  useEffect(() => {
    const nurNeuLaden = vorher.current.laden === laden && vorher.current.neuLaden !== p.neuLaden;
    vorher.current = { laden, neuLaden: p.neuLaden };
    void laden(1, nurNeuLaden ? seiteRef.current : 1);
  }, [laden, p.neuLaden]);

  // Funktionales Update (Review 19): Die Änderung wirkt auf den JÜNGSTEN Stand, nicht auf den des
  // letzten Zeichnens — zwei schnelle Wechsel hintereinander gehen so nicht verloren.
  const filterSetzen = (f: Partial<BoardFilter>) => {
    const aendern = (alt: BoardFilter): BoardFilter => {
      const neu = { ...alt, ...f };
      merkeFilter(neu);
      return neu;
    };
    if (p.onFilter !== undefined) p.onFilter(aendern);
    if (p.filter === undefined) setEigenerFilter(aendern);
  };

  const spaltenName = (id: string) => spalten.find((s) => s.id === id)?.name ?? "";
  // 204, 61l: Die Liste zählt zu den vier geteilten Filtern die Suche und das Archiv; „Zurücksetzen“
  // leert alle sechs (die vier über das Gedächtnis, damit es auch im Board gilt).
  const aktiv = anzahlAktiverFilter(filter) + (q !== "" ? 1 : 0) + (archiv ? 1 : 0);
  const zuruecksetzen = () => {
    filterSetzen(LEERER_FILTER);
    setQ("");
    setArchiv(false);
  };
  // Der Berliner Kalendertag (F13) — nicht der UTC-Tag aus toISOString.
  const heute = berlinerTag(new Date().toISOString());

  return (
    <section className={akte ? "tl tl--akte" : "tl"} aria-label="Ticket-Liste" tabIndex={-1}>
      {/* tabIndex -1: Fokus-Ersatz nach dem Schließen des Panels, wenn auch der Reiter keinen nimmt (F129). */}
      {akte ? (
        // In der Akte keine Werkzeugleiste: nur „Archiv einbeziehen“ wie bisher.
        <div className="tl__werkzeuge" role="toolbar" aria-label="Werkzeuge der Liste">
          <Optionen mehrfach beschriftung="Archiv" wert={archiv ? ["ja"] : []}
            onGeaendert={(d) => setArchiv((d.werte ?? []).includes("ja"))}>
            <option value="ja">Archiv einbeziehen</option>
          </Optionen>
        </div>
      ) : (
        <Werkzeugleiste className="tl__leiste" aria-label="Werkzeuge der Liste" aktiv={aktiv}
          onZurueckgesetzt={zuruecksetzen}
          filter={(
            <>
              <TicketFilterleiste client={p.client} companies={p.companies} ich={ich} tickets={items ?? []}
                filter={filter} onFilter={filterSetzen} />
              <Werkzeuggruppe beschriftung="Suche" aktiv={q !== ""}>
                <Suchfeld className="tl__suche" name="Tickets durchsuchen" platzhalter="Nummer oder Titel"
                  wert={q} onText={setQ} />
              </Werkzeuggruppe>
              <Werkzeuggruppe beschriftung="Archiv" aktiv={archiv}>
                <Knopf art="sekundaer" gestalt="pille" gedrueckt={archiv} onClick={() => setArchiv((a) => !a)}>
                  Archiv einbeziehen
                </Knopf>
              </Werkzeuggruppe>
            </>
          )}
          {...(p.onAnlegen === undefined ? {}
            : { haupt: <Knopf art="primaer" onClick={p.onAnlegen}>+ Aufgabe</Knopf> })} />
      )}
      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      <div className="tl__rollfeld">
        {items !== null && items.length === 0 && <p className="tl__leer">Keine Tickets.</p>}
        {items !== null && items.length > 0 && (
          <Tabelle stapeln dicht={akte}>
            <table>
              <thead>
                <tr>
                  {/* Akte kompakt (Review 19): ohne „Kunden“ (immer derselbe), „Alter“ und
                      „Verknüpfungen“ — fünf Spalten passen in die schmale Mittelspalte. */}
                  <th scope="col">Nummer</th><th scope="col">Titel</th>{!akte && <th scope="col">Kunden</th>}
                  <th scope="col">Zuständig</th><th scope="col">Spalte</th><th scope="col">Wiedervorlage</th>
                  {!akte && <th scope="col" data-ausrichtung="rechts">Alter</th>}
                  {!akte && <th scope="col">Verknüpfungen</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const marke = datumsMarke(t.resubmitDate, heute);
                  const farbe = hausfarbeChip(p.companies, t.companyShortCode);
                  return (
                    <tr key={t.id}>
                      <td className="tl__nummer">
                        {/* In der Akte trägt die Nummer das Unternehmen als Chip (die Kundenspalte fehlt). */}
                        {akte && <Chip className="tl__nummerchip" {...farbe}>{t.companyShortCode}</Chip>}
                        {t.ticketNumber}
                      </td>
                      <td className="tl__titel">
                        <KiKnopf art="leise" className="tl__titelknopf" onClick={() => p.onOeffnen(t.id)}>{t.title}</KiKnopf>
                      </td>
                      {!akte && <td>{t.customers.length === 0
                        ? <Chip {...farbe}>{t.companyShortCode}</Chip>
                        : t.customers.map((c) => (
                          <Chip key={c.id} {...farbe}>
                            <span className="tl__kundetext">{`${t.companyShortCode} · ${c.name}`}</span>
                          </Chip>
                        ))}</td>}
                      {/* In der Akte nur der Kreis (Name im Sofort-Popup und als zugänglicher Name) —
                          sonst passen fünf Spalten nicht in die Mittelspalte (Review 19). */}
                      <td><Avatar person={t.assignee} ich={t.assignee !== null && t.assignee.id === p.ichId}
                        {...(akte ? { ohneName: true } : {})} /></td>
                      <td className="tl__spalte">{spaltenName(t.columnId)}</td>
                      <td>{t.stateKind === "DONE" ? (t.status === "ARCHIVED" ? "archiviert" : "erledigt")
                        : marke.art === null
                          ? <Chip className={marke.ohne ? "tl__datum tl__datum--ohne" : "tl__datum"}>{marke.text}</Chip>
                          : <Chip statusArt={marke.art}>{marke.text}</Chip>}</td>
                      {!akte && <td data-ausrichtung="rechts">{tageZwischen(berlinerTag(t.createdAt), heute)} T</td>}
                      {!akte && <td>{t.linkCount}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Tabelle>
        )}
        {!akte && items !== null && mehr && (
          <KiKnopf onClick={() => { void laden(seite + 1); }}>Weitere laden</KiKnopf>
        )}
      </div>
    </section>
  );
}
